/* @flow */
import { trackStructuredEvent } from "shared/src/utils/TrackingUtil";
import TrackingState from "../stores/TrackingState";
import { TrackingTags } from "shared/src/tracking/Tracking";
import { PageType } from "shared/src/types/PageType";
import { ArticleSuggestionProperties, ArticleSuggestions } from "../datatypes/ArticleSuggestions";
import { cloneDeep, deepEqual } from "shared/src/utils/util";

const searchNameString = "search.";
const contentSearchNameString = "content.";

// trigger events
function trackSearchTrigger(query: string, initialSearch: boolean, typeDependentTags: TrackingTags) {
    const searchSuggestions = TrackingState.getLastSearchSuggestions();
    const suggestions = searchSuggestions.suggestions || [];
    const suggestionTags = {
        numSuggestions: suggestions.length,
        suggestions: suggestions.join(",")
    };

    const sanitizedQuery = query ? query.toLowerCase() : query;
    const searchTriggerTags = {
        query: sanitizedQuery,
        initialSearch: initialSearch,
        eventGroupId: TrackingState.getSuggestionEventGroupId()
    };

    const tags = {
        ...cloneDeep(searchTriggerTags),
        ...cloneDeep(suggestionTags),
        ...cloneDeep(typeDependentTags)
    };
    const structuredData = { tags };

    trackStructuredEvent(searchNameString + "trigger", structuredData);
    TrackingState.createNewSuggestionEventGroup();
}

export function trackStandardSearchTrigger(query: string, initialSearch: boolean, fsd: boolean) {
    const typeDependentTags = { type: "standardSearch", fsd };
    trackSearchTrigger(query, initialSearch, typeDependentTags);
}

export function trackCorrectedSearchTrigger(query: string, fsd: boolean) {
    const typeDependentTags = { type: "correctedSearch", fsd };
    trackSearchTrigger(query, false, typeDependentTags);
}

export function trackReplacementSearch(query: string) {
    const sanitizedQuery = query ? query.toLowerCase() : query;
    const tags = { query: sanitizedQuery, type: "replacementSearch" };
    const structuredData = { tags };
    trackStructuredEvent(searchNameString + "trigger", structuredData);
}

// result events
export function trackSearchResult(
    query: string,
    regularResults: number,
    selectedSortCriteria: string,
    pageNumber: number,
    usedOrSearch: boolean,
    ltrExternalModel?: string | undefined | null,
    autoCorrectedSolrQuery?: string | undefined | null
) {
    const sanitizedQuery = query ? query.toLowerCase() : query;
    const sanitizedAutoCorrectedSolrQuery = autoCorrectedSolrQuery
        ? autoCorrectedSolrQuery.toLowerCase()
        : autoCorrectedSolrQuery;
    const tags = {
        query: sanitizedQuery,
        autoCorrectedSolrQuery: sanitizedAutoCorrectedSolrQuery,
        regularResults,
        selectedSortCriteria,
        pageNumber,
        usedOrSearch,
        ltrExternalModel
    };
    const structuredData = { tags };
    trackStructuredEvent(searchNameString + "result", structuredData);
}

export function trackContentSearchResult(query: string, pageNumber: number, regularResults: number) {
    const sanitizedQuery = query ? query.toLowerCase() : query;
    const tags = {
        query: sanitizedQuery,
        pageNumber,
        regularResults
    };
    const structuredData = { tags };
    trackStructuredEvent(contentSearchNameString + "result", structuredData);
}

// filter events
function trackSearchFilter(tags: TrackingTags) {
    const structuredData = { tags };
    trackStructuredEvent(searchNameString + "filter", structuredData);
}

export function trackChooseCategory(name: string, query: string) {
    const sanitizedQuery = query ? query.toLowerCase() : query;
    const tags = { type: "chooseCategory", name, query: sanitizedQuery };
    trackSearchFilter(tags);
}

export function trackChooseHeaderCategory(name: string, query: string) {
    const sanitizedQuery = query ? query.toLowerCase() : query;
    const tags = { type: "chooseHeaderCategory", name, query: sanitizedQuery };
    trackSearchFilter(tags);
}

export function trackChooseSubCategoryPanel(name: string) {
    const tags = { type: "chooseSubCategoryPanel", name };
    trackSearchFilter(tags);
}

export function trackChooseFacet(key: string, name: string, newState: boolean) {
    const tags = { type: "chooseFacet", key, name, selected: newState };
    trackSearchFilter(tags);
}

export function trackChangeRangeFacet(
    key: string,
    minValue: number | undefined | null,
    maxValue: number | undefined | null
) {
    const tags = { type: "changeRangeFacet", key: key, minValue: minValue, maxValue: maxValue };
    trackSearchFilter(tags);
}

export function trackFilterFacets(query: string) {
    const tags = { type: "filterFacets", query: query };
    trackSearchFilter(tags);
}

export function trackDeselectFacet(key: string) {
    const tags = { type: "deselectFacet", key: key };
    trackSearchFilter(tags);
}

export function trackDeselectAllFacets() {
    const tags = { type: "deselectAllFacets" };
    trackSearchFilter(tags);
}

export function trackSearchFilterPanel(query: string) {
    const sanitizedQuery = query ? query.toLowerCase() : query;
    const tags = { type: "searchFilterPanel", query: sanitizedQuery };
    trackSearchFilter(tags);
}

export function trackSelectSortCriteria(key: string) {
    const tags = { selectedSortCriteria: key };
    const structuredData = { tags };
    trackStructuredEvent(searchNameString + "sort", structuredData);
}

export function trackBrokenSearchSorting(query: string, category: string, pageNumber: number, brokenItems: number) {
    const sanitizedQuery = query ? query.toLowerCase() : query;
    const tags = { query: sanitizedQuery, category, pageNumber, brokenItems };
    const structuredData = { tags };
    trackStructuredEvent(searchNameString + "sort.brokenSearchSorting", structuredData);
}

// next events
function trackSearchNext(tags: TrackingTags) {
    const structuredData = { tags };
    trackStructuredEvent(searchNameString + "next", structuredData);
}

export function trackNextPage(pageType: PageType, pageNumber: number) {
    const tags = { type: "page", pageType, pageNumber };
    trackSearchNext(tags);
}

function trackContentSearchNext(tags: TrackingTags) {
    const structuredData = { tags };
    trackStructuredEvent(contentSearchNameString + "next", structuredData);
}

export function trackNextContentPage(pageType: PageType, pageNumber: number) {
    const tags = { type: "contentPage", pageType, pageNumber };
    trackContentSearchNext(tags);
}

function trackVisibility(tags: TrackingTags) {
    const structuredData = { tags };
    trackStructuredEvent(searchNameString + "visible", structuredData);
}

export function trackToggleFacetGroup(key: string, type: string, visible: boolean) {
    const tags = { type: "toggleFacetGroup", key, typeOfFacet: type, visible };
    trackVisibility(tags);
}

export function trackToggleCategoryTree(visible: boolean) {
    const tags = { type: "toggleCategoryTree", visible };
    trackVisibility(tags);
}

export function trackToggleSearchField(visible: boolean) {
    const tags = { type: "toggleSearchField", visible };
    trackVisibility(tags);
}

export function trackToggleSubCategoryPanel(visible: boolean) {
    const tags = { type: "toggleSubCategoryPanel", visible };
    trackVisibility(tags);
}

export function trackChangeSearchContentTab(tab: "searchTab" | "contentTab") {
    const tags = { type: "toggleBetweenSearchAndContent", tab };
    trackVisibility(tags);
}

// suggestions events
function trackSuggestion(
    searchSuggestions: ArticleSuggestions,
    articleSuggestions: string[],
    linkSuggestions: string[]
) {
    const query: string = searchSuggestions.query;
    const suggestions: Array<string> = searchSuggestions.suggestions;
    const properties: ArticleSuggestionProperties[] | undefined | null = searchSuggestions.suggestionProperties;
    const numCategorySuggestions: number | null = properties ? properties.filter((s) => s.isCategory).length : null;
    const tags = {
        eventGroupId: TrackingState.getSuggestionEventGroupId(),
        query: query,
        numSuggestions: suggestions.length + articleSuggestions.length + linkSuggestions.length,
        numArticleSuggestions: articleSuggestions.length,
        numSearchSuggestions: suggestions.length,
        numCategorySuggestions: numCategorySuggestions,
        suggestions: suggestions.join(","),
        articleSuggestions: articleSuggestions.join(","),
        linkSuggestions: linkSuggestions.join(",")
    };
    const structuredData = { tags };
    trackStructuredEvent(searchNameString + "suggestion", structuredData);
}

export function trackSuggestionIfChanged(
    searchSuggestions: ArticleSuggestions,
    articleSuggestions: string[],
    linkSuggestions: string[]
) {
    if (
        !deepEqual(searchSuggestions, TrackingState.getLastSearchSuggestions()) ||
        !deepEqual(articleSuggestions, TrackingState.getLastArticleSuggestions()) ||
        !deepEqual(linkSuggestions, TrackingState.getLastLinkSuggestions())
    ) {
        trackSuggestion(searchSuggestions, articleSuggestions, linkSuggestions);
        TrackingState.setLastSearchSuggestions(searchSuggestions);
        TrackingState.setLastArticleSuggestions(articleSuggestions);
        TrackingState.setLastLinkSuggestions(linkSuggestions);
    }
}

// this tracks a *click* on the link suggestion
export function trackSelectedLinkSuggestion(
    link: string,
    suggestionIndex: number,
    indexWithinGroup: number,
    query: string,
    method: string,
    fsd: boolean
) {
    const tags = {
        to: link,
        query,
        suggestionIndex,
        indexWithinGroup,
        method,
        fsd
    };
    const structuredData = { tags };
    trackStructuredEvent(searchNameString + "linkSuggestion", structuredData);
}

export function trackSelectedSearchSuggestion(
    suggestion: string,
    suggestionIndex: number,
    indexWithinGroup: number,
    originalQuery: string,
    method: string,
    fsd: boolean,
    termType: string
) {
    const typeDependentTags = {
        type: "suggestedSearch",
        originalQuery,
        suggestionIndex,
        indexWithinGroup,
        method,
        fsd,
        termType
    };
    trackSearchTrigger(suggestion, true, typeDependentTags);
}

export function trackSelectedArticleSuggestion(
    suggestion: string,
    suggestionIndex: number,
    indexWithinGroup: number,
    query: string,
    method: string,
    fsd: boolean,
    bettyBundleId: string
) {
    const articleSuggestions = TrackingState.getLastArticleSuggestions();

    const sanitizedQuery = query ? query.toLowerCase() : query;
    const tags = {
        suggestionIndex,
        indexWithinGroup,
        method,
        fsd,
        bettyBundleId,
        // for the click event, we only include the respective section (articles in this case). This seems fine for now
        numSuggestions: articleSuggestions.length,
        articleSuggestions: articleSuggestions.join(","),
        query: sanitizedQuery,
        eventGroupId: TrackingState.getSuggestionEventGroupId()
    };

    const structuredData = { tags };

    trackStructuredEvent(searchNameString + "openArticleSuggestion", structuredData);
    TrackingState.createNewSuggestionEventGroup();
}

export function trackSelectedContentSuggestion(
    link: string,
    suggestionIndex: number,
    indexWithinGroup: number,
    query: string,
    method: string,
    fsd: boolean
) {
    const tags = {
        to: link,
        query,
        suggestionIndex,
        indexWithinGroup,
        method,
        fsd
    };
    const structuredData = { tags };
    trackStructuredEvent(searchNameString + "contentSuggestion", structuredData);
}

export function trackGiveSearchResultFeedback(
    feedbackString: string,
    query: string,
    employeeId: string,
    selectedCustomerId: string,
    resultIds: Array<string>,
    numberOfSearchResults: number
) {
    const tags = {
        feedbackString,
        query,
        employeeId,
        selectedCustomerId,
        resultIds,
        numberOfSearchResults
    };
    const structuredData = { tags };
    trackStructuredEvent(searchNameString + "searchResultFeedback", structuredData);
}
